// src/controllers/provisioningQRController.js
// ─────────────────────────────────────────────────────────────────────────────
import apiService    from '../services/apiService';
import ProvisioningQR from '../models/ProvisioningQR';

const unwrap = (res) => {
  if (!res) return {};
  if ('success' in res) return res;
  if (res.data && 'success' in res.data) return res.data;
  return res;
};

export const provisioningQRController = {

  // ── GET ALL ────────────────────────────────────────────────────────────────
  // GET /provisioning-qr?companyId=&type=&status=&page=&limit=
  getAll: async (filters = {}) => {
    try {
      const qp  = new URLSearchParams(filters).toString();
      const raw = unwrap(await apiService.get(qp ? `provisioning-qr?${qp}` : 'provisioning-qr'));
      const list = Array.isArray(raw.data) ? raw.data : [];
      return {
        success: true,
        data:    list.map(item => ProvisioningQR.fromJSON(item)),
        total:   raw.total || list.length,
      };
    } catch (error) {
      return { success: false, data: [], total: 0, message: error?.response?.data?.message || error.message };
    }
  },

  // ── GET BY ID ──────────────────────────────────────────────────────────────
  getById: async (id) => {
    try {
      const raw = unwrap(await apiService.get(`provisioning-qr/${id}`));
      return { success: true, data: ProvisioningQR.fromJSON(raw.data || raw) };
    } catch (error) {
      return { success: false, data: null, message: error?.response?.data?.message || error.message };
    }
  },

  // ── CREATE QR ──────────────────────────────────────────────────────────────
  // POST /provisioning-qr
  // Body: { name, description, type, companyId, appName, appPackage,
  //         groupId?, deviceLimit?, expiresAt? }
  create: async (data) => {
    try {
      const qr         = new ProvisioningQR(data);
      const validation = qr.validate();
      if (!validation.isValid) return { success: false, errors: validation.errors };

      const raw = unwrap(await apiService.post('provisioning-qr', qr.toJSON()));
      if (raw.success === false) return { success: false, errors: raw.errors || [], message: raw.message };

      return {
        success: true,
        data:    ProvisioningQR.fromJSON(raw.data || raw),
        message: raw.message || 'QR code generated successfully',
      };
    } catch (error) {
      const errData = error?.response?.data;
      return { success: false, message: errData?.message || error.message, errors: errData?.errors || [] };
    }
  },

  // ── UPDATE QR ──────────────────────────────────────────────────────────────
  // PUT /provisioning-qr/:id
  update: async (id, data) => {
    try {
      const qr         = new ProvisioningQR({ ...data, id });
      const validation = qr.validate();
      if (!validation.isValid) return { success: false, errors: validation.errors };

      const raw = unwrap(await apiService.put(`provisioning-qr/${id}`, qr.toJSON()));
      if (raw.success === false) return { success: false, errors: raw.errors || [], message: raw.message };

      return {
        success: true,
        data:    ProvisioningQR.fromJSON(raw.data || raw),
        message: raw.message || 'QR code updated successfully',
      };
    } catch (error) {
      const errData = error?.response?.data;
      return { success: false, message: errData?.message || error.message, errors: errData?.errors || [] };
    }
  },

  // ── TOGGLE ACTIVE ──────────────────────────────────────────────────────────
  // PATCH /provisioning-qr/:id/toggle
  toggle: async (id) => {
    try {
      const raw = unwrap(await apiService.patch(`provisioning-qr/${id}/toggle`, {}));
      return { success: true, data: ProvisioningQR.fromJSON(raw.data || raw), message: raw.message || 'Status toggled' };
    } catch (error) {
      return { success: false, message: error?.response?.data?.message || error.message };
    }
  },

  // ── REGENERATE QR IMAGE ────────────────────────────────────────────────────
  // POST /provisioning-qr/:id/regenerate
  regenerate: async (id) => {
    try {
      const raw = unwrap(await apiService.post(`provisioning-qr/${id}/regenerate`, {}));
      return { success: true, data: ProvisioningQR.fromJSON(raw.data || raw), message: raw.message || 'QR regenerated' };
    } catch (error) {
      return { success: false, message: error?.response?.data?.message || error.message };
    }
  },

  // ── DELETE ─────────────────────────────────────────────────────────────────
  delete: async (id) => {
    try {
      const raw = unwrap(await apiService.delete(`provisioning-qr/${id}`));
      return { success: true, message: raw.message || 'QR code deleted' };
    } catch (error) {
      return { success: false, message: error?.response?.data?.message || error.message };
    }
  },

  // ── GET ENROLLMENT LOGS ────────────────────────────────────────────────────
  // GET /provisioning-qr/:id/enrollments
  getEnrollments: async (id) => {
    try {
      const raw  = unwrap(await apiService.get(`provisioning-qr/${id}/enrollments`));
      const list = Array.isArray(raw.data) ? raw.data : [];
      return { success: true, data: list, total: raw.total || list.length };
    } catch (error) {
      return { success: false, data: [], message: error.message };
    }
  },

  // ── GET STATS ──────────────────────────────────────────────────────────────
  getStats: async (companyId) => {
    try {
      const raw = unwrap(await apiService.get(companyId ? `provisioning-qr/stats?companyId=${companyId}` : 'provisioning-qr/stats'));
      return { success: true, data: raw.data || raw };
    } catch (error) {
      return { success: false, data: null, message: error.message };
    }
  },

  // ── DOWNLOAD QR IMAGE ──────────────────────────────────────────────────────
  downloadQR: async (id, filename = 'qr-code.png') => {
    try {
      const raw = unwrap(await apiService.get(`provisioning-qr/${id}`));
      const qr  = ProvisioningQR.fromJSON(raw.data || raw);
      if (!qr.qrImageUrl) return { success: false, message: 'No QR image available' };

      const a = document.createElement('a');
      a.href     = qr.qrImageUrl;
      a.download = filename;
      a.click();
      return { success: true };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },
};

export default provisioningQRController;